package ro.horatiu_udrea.resursedeblocat

import kotlinx.coroutines.await
import kotlin.js.Promise

class JsPlatform : Platform {
    override val name: String = "Web with Kotlin/JS"
}

actual fun getPlatform(): Platform = JsPlatform()

actual fun executeGoogleLogin(
    onSuccess: (User) -> Unit,
    onFailure: (String) -> Unit
) {
    val success: (dynamic) -> Unit = { user ->
        onSuccess(User(
            id = user.uid as String,
            name = user.displayName as? String,
            email = user.email as? String,
            photoUrl = user.photoURL as? String,
            isAdmin = user.isAdmin as? Boolean ?: false
        ))
    }
    
    js("""
        window.loginWithGoogle().then(function(user) {
            console.log('Autentificat:', user.uid);
            success(user);
        }).catch(function(error) {
            console.error('Autentificare eșuată:', error);
            var errorCode = error.code || 'unknown';
            var errorMessage = 'A apărut o eroare la autentificare. Te rugăm să încerci din nou.';
            if (errorCode === 'auth/popup-closed-by-user' || errorCode === 'auth/cancelled-popup-request') {
                errorMessage = 'Autentificarea a fost anulată.';
            }
            onFailure(errorMessage);
        });
    """)
}

actual fun onAuthStateChanged(onUserChanged: (User?) -> Unit) {
    val callback: (dynamic) -> Unit = { user ->
        if (user != null) {
            onUserChanged(User(
                id = user.uid as String,
                name = user.displayName as? String,
                email = user.email as? String,
                photoUrl = user.photoURL as? String,
                isAdmin = user.isAdmin as? Boolean ?: false
            ))
        } else {
            onUserChanged(null)
        }
    }
    js("window.onAuthStateChanged(callback);")
}

actual fun executeLogout() {
    js("window.logout();")
}

actual fun getResources(
    userId: String,
    onSuccess: (List<ResourceDetails>) -> Unit,
    onFailure: (String) -> Unit
) {
    val success: (dynamic) -> Unit = { list ->
        try {
            val result = mutableListOf<ResourceDetails>()
            val array = list as Array<dynamic>
            for (item in array) {
                try {
                    result.add(ResourceDetails(
                        id = item.id as String,
                        sheetId = item.sheetId as? String ?: "",
                        nume = item.nume as? String ?: "Fără nume",
                        tip = item.tip as? String ?: "Resursă",
                        autor = item.autor as? String ?: "Autor necunoscut",
                        link = item.link as? String ?: "",
                        imagine = item.imagine as? String,
                        categorii_match = run {
                            val cats = item.categorii ?: item.categorii_match
                            when {
                                cats is Array<*> -> {
                                    val arr = cats as Array<dynamic>
                                    val list = mutableListOf<String>()
                                    for (i in 0 until (arr.length as Int)) {
                                        list.add((arr[i] ?: "").toString())
                                    }
                                    list
                                }
                                js("typeof cats === 'string'") as Boolean -> {
                                    val s = cats as String
                                    if (s.isBlank()) emptyList() else s.split(",").map { it.trim() }
                                }
                                else -> emptyList()
                            }
                        },
                        despre_resursa = item.despre_resursa as? String ?: "",
                        despre_autor = item.despre_autor as? String ?: "",
                        parcurs_personal = item.parcurs_personal as? String ?: "",
                        recomandat_daca = item.recomandat_daca as? String ?: "",
                        sourceSubcategory = item.sourceSubcategory as? String ?: "",
                        addedAt = run {
                            val t = item.addedAt
                            if (t != null && t != undefined) {
                                if (js("typeof t.toMillis === 'function'") as Boolean) {
                                    (t.toMillis() as Double).toLong()
                                } else if (js("typeof t === 'number'") as Boolean) {
                                    (t as Double).toLong()
                                } else 0L
                            } else 0L
                        },
                        state = ResourceState.valueOf(item.state as? String ?: "FRESH")
                    ))
                } catch (e: Exception) {
                    println("Error parsing individual resource: ${e.message}")
                    // Log the raw item to see what failed
                    val itemStr = js("JSON.stringify(item)")
                    println("Raw item: $itemStr")
                }
            }
            onSuccess(result)
        } catch (e: Exception) {
            println("Critical error in getResources success callback: ${e.message}")
            onFailure(e.message ?: "Parsing error")
        }
    }

    js("""
        window.getResources(userId).then(function(list) {
            success(list);
        }).catch(function(error) {
            onFailure(error.message || 'Error getting resources');
        });
    """)
}

actual fun addResource(
    userId: String,
    resource: ResourceDetails,
    onSuccess: (String) -> Unit,
    onFailure: (String) -> Unit
) {
    val resourceJs = js("{}")
    resourceJs.sheetId = resource.sheetId
    resourceJs.nume = resource.nume
    resourceJs.tip = resource.tip
    resourceJs.autor = resource.autor
    resourceJs.link = resource.link
    resourceJs.imagine = resource.imagine
    resourceJs.categorii_match = resource.categorii_match.toTypedArray()
    resourceJs.despre_resursa = resource.despre_resursa
    resourceJs.despre_autor = resource.despre_autor
    resourceJs.parcurs_personal = resource.parcurs_personal
    resourceJs.recomandat_daca = resource.recomandat_daca
    resourceJs.sourceSubcategory = resource.sourceSubcategory
    resourceJs.state = resource.state.name

    js("""
        window.addResource(userId, resourceJs).then(function(id) {
            onSuccess(id);
        }).catch(function(error) {
            onFailure(error.message || 'Error adding resource');
        });
    """)
}

actual fun updateResourceState(
    userId: String,
    resourceId: String,
    newState: ResourceState,
    onSuccess: () -> Unit,
    onFailure: (String) -> Unit
) {
    val updates = js("{}")
    updates.state = newState.name

    val success: () -> Unit = {
        try {
            onSuccess()
            println("onSuccess callback executed")
        } catch (e: Exception) {
            println("Error in onSuccess callback: ${e.message}")
        }
    }
    
    val failure: (String) -> Unit = { error ->
        try {
            onFailure(error)
            println("onFailure callback executed")
        } catch (e: Exception) {
            println("Error in onFailure callback: ${e.message}")
        }
    }

    js("""
        console.log('Calling window.updateResource', userId, resourceId, updates);
        window.updateResource(userId, resourceId, updates).then(function() {
            console.log('window.updateResource success');
            success();
        }).catch(function(error) {
            console.error('window.updateResource error', error);
            failure(error.message || 'Error updating resource');
        });
    """)
}

actual fun deleteResource(
    userId: String,
    resourceId: String,
    onSuccess: () -> Unit,
    onFailure: (String) -> Unit
) {
    js("""
        console.log('Calling window.deleteResource', userId, resourceId);
        window.deleteResource(userId, resourceId).then(function() {
            console.log('window.deleteResource success');
            try {
                onSuccess();
            } catch (e) {
                console.error('Error in onSuccess callback', e);
            }
        }).catch(function(error) {
            console.error('window.deleteResource error', error);
            try {
                onFailure(error.message || 'Error deleting resource');
            } catch (e) {
                console.error('Error in onFailure callback', e);
            }
        });
    """)
}

actual fun recommendResource(
    subcategory: String,
    context: String,
    resourceTypes: List<String>,
    onSuccess: (ResourceDetails) -> Unit,
    onFailure: (String) -> Unit
) {
    val data = js("{}")
    data.subcategory = subcategory
    data.context = context
    data.resourceTypes = resourceTypes.toTypedArray()

    val success: (dynamic) -> Unit = { item ->
        try {
            onSuccess(ResourceDetails(
                id = item.id as String,
                sheetId = item.sheetId as? String ?: "",
                nume = item.nume as? String ?: "Fără nume",
                tip = item.tip as? String ?: "Resursă",
                autor = item.autor as? String ?: "Autor necunoscut",
                link = item.link as? String ?: "",
                imagine = item.imagine as? String,
                categorii_match = run {
                    val cats = item.categorii ?: item.categorii_match
                    when {
                        cats is Array<*> -> {
                            val arr = cats as Array<dynamic>
                            val list = mutableListOf<String>()
                            for (i in 0 until (arr.length as Int)) {
                                list.add((arr[i] ?: "").toString())
                            }
                            list
                        }
                        js("typeof cats === 'string'") as Boolean -> {
                            val s = cats as String
                            if (s.isBlank()) emptyList() else s.split(",").map { it.trim() }
                        }
                        else -> emptyList()
                    }
                },
                despre_resursa = item.despre_resursa as? String ?: "",
                despre_autor = item.despre_autor as? String ?: "",
                parcurs_personal = item.parcurs_personal as? String ?: "",
                recomandat_daca = item.recomandat_daca as? String ?: "",
                sourceSubcategory = item.sourceSubcategory as? String ?: "",
                addedAt = run {
                    val t = item.addedAt
                    if (t != null && t != undefined) {
                        if (js("typeof t.toMillis === 'function'") as Boolean) {
                            (t.toMillis() as Double).toLong()
                        } else if (js("typeof t === 'number'") as Boolean) {
                            (t as Double).toLong()
                        } else 0L
                    } else 0L
                },
                state = ResourceState.valueOf(item.state as? String ?: "FRESH")
            ))
        } catch (e: Exception) {
            println("Error parsing recommendResource result: ${e.message}")
            val itemStr = js("JSON.stringify(item)")
            println("Raw result: $itemStr")
            onFailure("Parsing error: ${e.message}")
        }
    }

    js("""
        window.recommendResource(data).then(function(result) {
            success(result);
        }).catch(function(error) {
            onFailure(error.message || 'Error calling recommendResource');
        });
    """)
}

actual fun getCurrentMonthYear(): MonthYear {
    val date = js("new Date()")
    return MonthYear((date.getMonth() as Int) + 1, date.getFullYear() as Int)
}

actual fun openUrl(url: String) {
    js("window.open(url, '_blank');")
}

actual suspend fun getRemoteConfigString(key: String, defaultValue: String): String {
    return try {
        val promise = js("window.getRemoteConfigString(key, defaultValue)") as Promise<String>
        promise.await()
    } catch (e: Exception) {
        println("Error getting remote config: ${e.message}")
        defaultValue
    }
}

actual suspend fun resolveImageUrl(pathOrUrl: String): String {
    return pathOrUrl
}

actual fun getTrackings(
    userId: String,
    onSuccess: (List<MonthlyTracking>) -> Unit,
    onFailure: (String) -> Unit
) {
    val success: (dynamic) -> Unit = { list ->
        try {
            val result = mutableListOf<MonthlyTracking>()
            val array = list as Array<dynamic>
            for (item in array) {
                try {
                    val answers = mutableMapOf<String, Any>()
                    val answersJs = item.answers
                    if (answersJs != null && answersJs != undefined) {
                        val keys = js("Object.keys(answersJs)") as Array<String>
                        for (key in keys) {
                            val v = answersJs[key]
                            answers[key] = when {
                                js("Array.isArray(v)") as Boolean -> {
                                    val arr = v as Array<dynamic>
                                    val list = mutableListOf<String>()
                                    for (i in 0 until (arr.length as Int)) {
                                        list.add((arr[i] ?: "").toString())
                                    }
                                    list
                                }
                                js("typeof v === 'number'") as Boolean -> (v as Double).toInt()
                                else -> (v ?: "").toString()
                            }
                        }
                    }

                    result.add(MonthlyTracking(
                        id = item.id as String,
                        month = (item.month as Double).toInt(),
                        year = (item.year as Double).toInt(),
                        answers = answers,
                        completed = item.completed as? Boolean ?: false,
                        updatedAt = run {
                            val t = item.updatedAt
                            if (t != null && t != undefined) {
                                if (js("typeof t.toMillis === 'function'") as Boolean) {
                                    (t.toMillis() as Double).toLong()
                                } else if (js("typeof t === 'number'") as Boolean) {
                                    (t as Double).toLong()
                                } else 0L
                            } else 0L
                        }
                    ))
                } catch (e: Exception) {
                    println("Error parsing tracking item: ${e.message}")
                }
            }
            onSuccess(result)
        } catch (e: Exception) {
            onFailure(e.message ?: "Parsing error")
        }
    }

    val failure: (dynamic) -> Unit = { error ->
        onFailure(error.message?.toString() ?: "Error getting trackings")
    }

    js("""
        window.getTrackings(userId).then(function(list) {
            success(list);
        }).catch(function(error) {
            failure(error);
        });
    """)
}

actual fun saveTracking(
    userId: String,
    tracking: MonthlyTracking,
    onSuccess: (String) -> Unit,
    onFailure: (String) -> Unit
) {
    val trackingJs = js("{}")
    if (tracking.id.isNotEmpty()) trackingJs.id = tracking.id
    trackingJs.month = tracking.month
    trackingJs.year = tracking.year
    trackingJs.completed = tracking.completed
    
    val answersJs = js("{}")
    tracking.answers.forEach { (k, v) ->
        when (v) {
            is List<*> -> {
                val arr = js("[]")
                (v as List<*>).forEach { item -> arr.push(item) }
                answersJs[k] = arr
            }
            else -> answersJs[k] = v
        }
    }
    trackingJs.answers = answersJs

    val failure: (dynamic) -> Unit = { error ->
        onFailure(error.message?.toString() ?: "Error saving tracking")
    }

    js("""
        window.saveTracking(userId, trackingJs).then(function(id) {
            onSuccess(id);
        }).catch(function(error) {
            failure(error);
        });
    """)
}

actual fun getAllUsers(
    onSuccess: (List<User>) -> Unit,
    onFailure: (String) -> Unit
) {
    val success: (Array<dynamic>) -> Unit = { list ->
        try {
            val result = list.map { u ->
                User(
                    id = u.uid as String,
                    name = u.name as? String,
                    email = u.email as? String,
                    photoUrl = u.photoURL as? String,
                    isAdmin = u.admin as? Boolean ?: false
                )
            }
            onSuccess(result)
        } catch (e: Exception) {
            onFailure(e.message ?: "Parsing error")
        }
    }

    js("""
        window.getAllUsers().then(function(list) {
            success(list);
        }).catch(function(error) {
            onFailure(error.message || 'Error getting all users');
        });
    """)
}

actual fun getUserResources(
    targetUserId: String,
    onSuccess: (List<ResourceDetails>) -> Unit,
    onFailure: (String) -> Unit
) {
    val success: (Array<dynamic>) -> Unit = { list ->
        try {
            val result = list.map { item ->
                ResourceDetails(
                    id = item.id as String,
                    sheetId = item.sheetId as? String ?: "",
                    nume = item.nume as? String ?: "Fără nume",
                    tip = item.tip as? String ?: "Resursă",
                    autor = item.autor as? String ?: "Autor necunoscut",
                    link = item.link as? String ?: "",
                    imagine = item.imagine as? String,
                    categorii_match = run {
                        val cats = item.categorii ?: item.categorii_match
                        when {
                            cats is Array<*> -> {
                                val arr = cats as Array<dynamic>
                                val list = mutableListOf<String>()
                                for (i in 0 until (arr.length as Int)) {
                                    list.add((arr[i] ?: "").toString())
                                }
                                list
                            }
                            js("typeof cats === 'string'") as Boolean -> {
                                val s = cats as String
                                if (s.isBlank()) emptyList() else s.split(",").map { it.trim() }
                            }
                            else -> emptyList()
                        }
                    },
                    despre_resursa = item.despre_resursa as? String ?: "",
                    despre_autor = item.despre_autor as? String ?: "",
                    parcurs_personal = item.parcurs_personal as? String ?: "",
                    recomandat_daca = item.recomandat_daca as? String ?: "",
                    sourceSubcategory = item.sourceSubcategory as? String ?: "",
                    addedAt = run {
                        val t = item.addedAt
                        if (t != null && t != undefined) {
                            if (js("typeof t.toMillis === 'function'") as Boolean) {
                                (t.toMillis() as Double).toLong()
                            } else if (js("typeof t === 'number'") as Boolean) {
                                (t as Double).toLong()
                            } else 0L
                        } else 0L
                    },
                    state = ResourceState.valueOf(item.state as? String ?: "FRESH")
                )
            }
            onSuccess(result)
        } catch (e: Exception) {
            onFailure(e.message ?: "Parsing error")
        }
    }

    js("""
        window.getUserResources(targetUserId).then(function(list) {
            success(list);
        }).catch(function(error) {
            onFailure(error.message || 'Error getting user resources');
        });
    """)
}

actual fun getUserTrackings(
    targetUserId: String,
    onSuccess: (List<MonthlyTracking>) -> Unit,
    onFailure: (String) -> Unit
) {
    val success: (Array<dynamic>) -> Unit = { list ->
        try {
            val result = parseTrackings(list)
            onSuccess(result)
        } catch (e: Exception) {
            onFailure(e.message ?: "Parsing error")
        }
    }

    js("""
        window.getUserTrackings(targetUserId).then(function(list) {
            success(list);
        }).catch(function(error) {
            onFailure(error.message || 'Error getting user trackings');
        });
    """)
}

actual fun listenToUserTrackings(
    targetUserId: String,
    onUpdate: (List<MonthlyTracking>) -> Unit,
    onFailure: (String) -> Unit
): () -> Unit {
    val update: (Array<dynamic>) -> Unit = { list ->
        try {
            val result = parseTrackings(list)
            onUpdate(result)
        } catch (e: Exception) {
            onFailure(e.message ?: "Parsing error")
        }
    }

    val unsubscribe = js("""
        window.listenToUserTrackings(targetUserId, update, onFailure)
    """)

    return {
        js("unsubscribe()")
    }
}

private fun parseTrackings(list: Array<dynamic>): List<MonthlyTracking> {
    return list.map { item ->
        val answers = mutableMapOf<String, Any>()
        val answersJs = item.answers
        if (answersJs != null && answersJs != undefined) {
            val keys = js("Object.keys(answersJs)") as Array<String>
            for (key in keys) {
                val v = answersJs[key]
                answers[key] = when {
                    js("Array.isArray(v)") as Boolean -> {
                        val arr = v as Array<dynamic>
                        val list = mutableListOf<String>()
                        for (i in 0 until (arr.length as Int)) {
                            list.add((arr[i] ?: "").toString())
                        }
                        list
                    }
                    js("typeof v === 'number'") as Boolean -> (v as Double).toInt()
                    else -> (v ?: "").toString()
                }
            }
        }

        MonthlyTracking(
            id = item.id as String,
            month = (item.month as Double).toInt(),
            year = (item.year as Double).toInt(),
            answers = answers,
            completed = item.completed as? Boolean ?: false,
            updatedAt = run {
                val t = item.updatedAt
                if (t != null && t != undefined) {
                    if (js("typeof t.toMillis === 'function'") as Boolean) {
                        (t.toMillis() as Double).toLong()
                    } else if (js("typeof t === 'number'") as Boolean) {
                        (t as Double).toLong()
                    } else 0L
                } else 0L
            }
        )
    }
}